import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Daily Personal Activities & Care",
  "Community Participation",
  "Supported Independent Living (SIL)",
  "Household Tasks",
  "Transport Assistance",
  "Support Coordination",
  "Not sure — please advise",
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  ndis_number: "",
  service: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        ndis_number: form.ndis_number || null,
      });
      setSuccess(true);
      setForm(initialForm);
      toast.success("Thanks — we’ll be in touch within one business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again or call us directly."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-28 bg-brand-bone"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-2 gap-12 lg:gap-16">
        <div>
          <span className="text-xs uppercase tracking-[0.22em] text-brand-terracotta font-semibold">
            Get in touch
          </span>
          <h2 className="mt-3 font-heading font-semibold tracking-tight text-brand-forest text-3xl sm:text-4xl lg:text-5xl">
            Let’s start a conversation.
          </h2>
          <p className="mt-5 text-base lg:text-lg text-brand-muted leading-relaxed max-w-lg">
            Tell us about yourself and what you’re looking for. We’ll respond within one
            business day — no pressure, no obligation.
          </p>

          <div className="mt-10 space-y-4">
            <div data-testid="contact-info-phone" className="flex items-start gap-4">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-forest/10 text-brand-forest">
                <Phone className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-brand-muted">Phone</p>
                <a href="tel:0413918449" className="font-semibold text-brand-ink hover:text-brand-forest">
                  0413 918 449
                </a>
              </div>
            </div>
            <div data-testid="contact-info-email" className="flex items-start gap-4">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-forest/10 text-brand-forest">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-brand-muted">Email</p>
                <a
                  href="mailto:joseph@guidingsupportservices.com"
                  className="font-semibold text-brand-ink hover:text-brand-forest"
                >
                  joseph@guidingsupportservices.com
                </a>
              </div>
            </div>
            <div data-testid="contact-info-office" className="flex items-start gap-4">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-forest/10 text-brand-forest">
                <MapPin className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-brand-muted">Head office</p>
                <p className="font-semibold text-brand-ink">Melbourne, VIC</p>
              </div>
            </div>
          </div>

          <div className="mt-10 p-6 rounded-2xl bg-brand-forest text-brand-bone">
            <p className="font-heading text-lg font-semibold">After‑hours support</p>
            <p className="text-sm mt-2 text-brand-bone/85 leading-relaxed">
              Our on‑call team is available 24/7 for active participants. Call your usual
              line and follow the prompts.
            </p>
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={onSubmit}
          className="bg-white border border-brand-sand rounded-2xl p-7 lg:p-10 shadow-sm relative"
        >
          {success && (
            <div
              data-testid="contact-success"
              className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-2xl grid place-items-center text-center p-10 z-10"
            >
              <div>
                <div className="mx-auto h-14 w-14 rounded-full bg-brand-forest/10 text-brand-forest grid place-items-center">
                  <CheckCircle2 className="h-7 w-7" />
                </div>
                <h3 className="mt-5 font-heading text-2xl font-semibold text-brand-forest">
                  Message received
                </h3>
                <p className="mt-2 text-sm text-brand-muted max-w-sm">
                  Thank you. A member of our team will reach out within one business day to
                  the contact details you provided.
                </p>
                <Button
                  data-testid="contact-send-another"
                  onClick={() => setSuccess(false)}
                  className="mt-6 bg-brand-forest hover:bg-brand-forestDark text-brand-bone rounded-full"
                  type="button"
                >
                  Send another message
                </Button>
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-sm font-medium text-brand-ink">
                Full name <span className="text-brand-terracotta">*</span>
              </Label>
              <Input
                id="name"
                data-testid="contact-input-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Alex Morgan"
                className="bg-brand-bone border-brand-sand focus-visible:ring-brand-forest focus-visible:ring-offset-0 h-11"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-brand-ink">
                Email <span className="text-brand-terracotta">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="contact-input-email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="you@example.com"
                className="bg-brand-bone border-brand-sand focus-visible:ring-brand-forest focus-visible:ring-offset-0 h-11"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone" className="text-sm font-medium text-brand-ink">
                Phone <span className="text-brand-terracotta">*</span>
              </Label>
              <Input
                id="phone"
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="04xx xxx xxx"
                className="bg-brand-bone border-brand-sand focus-visible:ring-brand-forest focus-visible:ring-offset-0 h-11"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ndis_number" className="text-sm font-medium text-brand-ink">
                NDIS number <span className="text-brand-muted/70 font-normal">(optional)</span>
              </Label>
              <Input
                id="ndis_number"
                data-testid="contact-input-ndis"
                value={form.ndis_number}
                onChange={(e) => update("ndis_number", e.target.value)}
                placeholder="430 000 000"
                className="bg-brand-bone border-brand-sand focus-visible:ring-brand-forest focus-visible:ring-offset-0 h-11"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="service" className="text-sm font-medium text-brand-ink">
                Service of interest <span className="text-brand-terracotta">*</span>
              </Label>
              <Select
                value={form.service}
                onValueChange={(v) => update("service", v)}
              >
                <SelectTrigger
                  id="service"
                  data-testid="contact-select-service"
                  className="bg-brand-bone border-brand-sand focus:ring-brand-forest focus:ring-offset-0 h-11"
                >
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="message" className="text-sm font-medium text-brand-ink">
                Tell us about your situation <span className="text-brand-terracotta">*</span>
              </Label>
              <Textarea
                id="message"
                data-testid="contact-input-message"
                rows={5}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="A short paragraph helps us match you with the right team member."
                className="bg-brand-bone border-brand-sand focus-visible:ring-brand-forest focus-visible:ring-offset-0"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="contact-submit-button"
            disabled={loading}
            className="mt-7 w-full sm:w-auto bg-brand-forest hover:bg-brand-forestDark text-brand-bone rounded-full px-7 py-6 text-base font-semibold"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                Send message
                <Send className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
          <p className="mt-4 text-xs text-brand-muted">
            By submitting this form you agree to our friendly team contacting you about your enquiry.
          </p>
        </form>
      </div>
    </section>
  );
}
